import { Form, Formik } from "formik";
import React, { FC, ReactElement, useState } from "react";
import { toast } from "react-toastify";
import { useDialog } from '../../../components/dialog/DialogContext';
import { DAILY_OPTION, PATTERN, RECURRENCE } from "../../../constants/notification-key";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import EnrolmentColumns from "../../../models/ColumnDefinitions/EnrolmentColumns";
import { ScheduleNotification } from "../../../models/ScheduleNotification";
import EnrolmentSearch from "../../../models/search/EnrolmentSearch";
import { hideColumns, hideLookups } from "../../../models/utilities/columnHelper";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import { saveScheduledNotification } from "../../../services/notificationService";
import { convertDateToString, getFormattedTime, getTime } from "../../../services/utilities/dateFormatting";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { convertDateToNumber, convertNumberToDate } from "../../../utilities/DateUtils";
import CcRecipientStep from "../cc-recipient-step";
import EmailTemplateForm from "../email-step";
import NotificationNameStep from "../notification-name-step";
import NotificationWizard from "../notification-wizard";
import ReportStep from "../report-step";
import ToRecipientStep from "../to-recipient-step";
import ScheduleStep from "./steps/schedule-step";
import Summary from "./steps/summary";

interface Props {
    onClose: () => void;
    defaultSteps: WizardSideBarStep[];
    defaultContent: ReactElement[];
    onBack: (event: any) => void;
}

const ScheduleModal: FC<Props> = ({ onClose, defaultSteps, defaultContent, onBack }) => {
    const { translate } = useLocale();
    const [step, setStep] = useState<number>(1);
    const dialog = useDialog();
    const startDate = convertNumberToDate(convertDateToNumber(new Date()))
    const initialItem: ScheduleNotification = {
        vruleActionId: 0,
        name: "",
        id: 0,
        time: "",
        daysOfTheWeek: [],
        timeZone: "",
        timeZoneId: "1",
        pattern: PATTERN.DAILY,
        startDate: startDate,
        occurrences: "1",
        type: "",
        toRecipients: [],
        ccRecipients: [],
        emailAccount: "",
        emailAccountId: "",
        subject: "",
        body: "",
        disclaimer: translate.byKey(TranslationKeys.dontReplyToThisEmailAddress),
        report: "",
        reportId: "",
        recurrenceOption: RECURRENCE.NO_END_DATE,
        typeId: "",
        dailyOption: DAILY_OPTION.EVERY,
        settingDatasRef: [],
        enabled: 0,
        lastTriggered: ""
    };
    const columns = hideLookups(hideColumns(EnrolmentColumns, [
        "totalTags",
        "category",
        "uid",
        "gender",
        "title",
        "totalAccessGroups",
        "suffix",
        "enabled",
        "masterTypeName",
        "site",
    ]), ['name']);

    const [sideBarSteps, setSideBarSteps] = useState([
        ...defaultSteps,
        {
            title: translate.byKey(TranslationKeys.name),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.report),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.sendTo),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.copyTo),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.schedule),
            description: "",
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.template),
            description: translate.byKey(TranslationKeys.pending),
            substeps: [],
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: "",
            substeps: [],
        },
    ]);
   

    const contentPages: ReactElement[] = [
        ...defaultContent,
        <NotificationNameStep
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setSideBarStepDescription={(newDescription) =>
                setSideBarStepDescription(1, newDescription)
            }
        />,
        <ReportStep
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setDescription={(newDescription) =>
                setSideBarStepDescription(2, newDescription)
            } />,
        <ToRecipientStep
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setDescription={(newDescription: string) =>
                setSideBarStepDescription(3, newDescription)
            }
            columnDescriptors={{
                columns: columns,
                searchColumns: EnrolmentSearch,
            }} />,
        <CcRecipientStep
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setDescription={(newDescription: string) =>
                setSideBarStepDescription(4, newDescription)
            }
            columnDescriptors={{
                columns: columns,
                searchColumns: EnrolmentSearch,
            }} />,
        <ScheduleStep
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)} />,
        <EmailTemplateForm
            showPreview={false}
            onNext={(event) => onLocalNext(event)}
            onBack={(event) => onLocalBack(event)}
            setSideBarStepDescription={(newDescription) =>
                setSideBarStepDescription(6, newDescription)
            } />,
        <Summary onBack={(event) => onLocalBack(event)} taskDescription={sideBarSteps[0].description} />,
    ];

    

    const onLocalNext = (event) => {
        setStep(step + 1);
    };

    const onLocalBack = (event: any) => {
        if (step === 1) {
            onBack(event)
            return;
        }
        setStep(step - 1);
    };

    const setSideBarStepDescription = (index, newDescription) => {
        const newSideBarSteps = sideBarSteps.map((step, stepIndex) => {
            return {
                ...step,
                description:
                    stepIndex === index ? newDescription : step.description,
            };
        });
        setSideBarSteps(newSideBarSteps);
    };

    const onSaveNotification = (values) => {
        const updatedValues = {
            ...values,
            daysOfTheWeek: values.dailyOption && values.dailyOption === DAILY_OPTION.EVERY_WEEKDAY ?
                ["MON", "TUE", "WED", "THU", "FRI"] :
                values.daysOfTheWeek,
            endDate: convertDateToString(values.endDate),
            startDate: convertDateToString(values.startDate),
            time: getFormattedTime(getTime(values.time))
        }

        saveScheduledNotification(updatedValues).then((value) => {
            toast.success(translate.byKey(TranslationKeys.success));
            onClose()
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        })
    }

    return(
        <Formik
            enableReinitialize={true}
            initialValues={initialItem}
            onSubmit={onSaveNotification}>
            <Form
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                    }
                }
            }>
            {/* This wizard is a special case as we are making a
                 selection between 2 different sets of Wizard steps to use
                 hence why we had to create a separate wizard component rather than
                 using the standard WizardTwo that is used everywhere else */}
                <NotificationWizard
                    sidebarSteps={sideBarSteps}
                    step={step}
                    name={translate.byKey(TranslationKeys.notifications)}
                    onClose={onClose}
                    stepContent={contentPages} />
            </Form>
        </Formik>
    )
}

export default ScheduleModal;